if (!customElements.get('and-product-compare')) {
  customElements.define('and-product-compare', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort(); this.observer?.disconnect();
      this.controller = new AbortController();
      const initialize = () => {
        if (!this.isConnected) return;
        this.scroller = this.querySelector('[data-compare-scroll]');
        if (!this.scroller) return;
        const options = { signal: this.controller.signal };
        this.addEventListener('click', event => {
          const previous = event.target.closest('[data-compare-prev]'), next = event.target.closest('[data-compare-next]');
          if (previous || next) this.move(previous ? -1 : 1);
        }, options);
        this.scroller.addEventListener('scroll', () => this.update(), { ...options, passive: true });
        this.scroller.addEventListener('pointerdown', event => {
          if (event.pointerType === 'touch' || event.button !== 0 || event.target.closest('a,button,input,select,textarea')) return;
          this.startDrag(event.clientX, event.clientY, event.timeStamp, event.pointerId);
        }, options);
        this.scroller.addEventListener('pointermove', event => {
          if (event.pointerType === 'touch' || !this.drag || this.drag.pointerId !== event.pointerId) return;
          if (this.dragTo(event.clientX, event.clientY, event.timeStamp)) {
            if (!this.scroller.hasPointerCapture(event.pointerId)) this.scroller.setPointerCapture(event.pointerId);
            event.preventDefault();
          }
        }, options);
        const finishPointer = event => { if (this.drag && this.drag.pointerId === event.pointerId) this.endDrag(); };
        this.scroller.addEventListener('pointerup', finishPointer, options);
        this.scroller.addEventListener('pointercancel', finishPointer, options);
        this.scroller.addEventListener('lostpointercapture', finishPointer, options);
        this.scroller.addEventListener('touchstart', event => {
          if (event.touches.length !== 1) { this.drag = null; return; }
          const touch = event.touches[0]; this.startDrag(touch.clientX, touch.clientY, event.timeStamp, 'touch');
        }, { ...options, passive: true, capture: true });
        this.scroller.addEventListener('touchmove', event => {
          if (!this.drag || this.drag.pointerId !== 'touch' || event.touches.length !== 1) return;
          const touch = event.touches[0];
          if (this.dragTo(touch.clientX, touch.clientY, event.timeStamp)) {
            if (event.cancelable) event.preventDefault();
            event.stopPropagation();
          }
        }, { ...options, passive: false, capture: true });
        this.scroller.addEventListener('touchend', () => { if (this.drag?.pointerId === 'touch') this.endDrag(); }, options);
        this.scroller.addEventListener('touchcancel', () => { this.drag = null; this.scroller.classList.remove('is-dragging'); }, options);
        this.scroller.addEventListener('click', event => {
          if (this.draggedAt && performance.now() - this.draggedAt < 250) { event.preventDefault(); event.stopImmediatePropagation(); }
        }, { ...options, capture: true });

        this.scroller.addEventListener('keydown', event => {
          if (event.target !== this.scroller) return;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); this.move(event.key === 'ArrowLeft' ? -1 : 1); }
          if (event.key === 'Home' || event.key === 'End') { event.preventDefault(); cancelAnimationFrame(this.momentum); this.scroller.scrollLeft = event.key === 'Home' ? 0 : this.scroller.scrollWidth; }
        }, options);
        this.observer = new ResizeObserver(() => this.update());
        this.observer.observe(this.scroller);
        const table = this.querySelector('table'); if (table) this.observer.observe(table);
        this.update();
      };
      if (this.querySelector('[data-compare-scroll]')) initialize(); else requestAnimationFrame(initialize);
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); cancelAnimationFrame(this.momentum); this.drag = null; }
    startDrag(x, y, time, pointerId) {
      cancelAnimationFrame(this.momentum);
      this.drag = { x, y, left: this.scroller.scrollLeft, lastX: x, time, velocity: 0, axis: null, pointerId };
    }
    dragTo(x, y, time) {
      const drag = this.drag;
      if (!drag || this.scroller.scrollWidth <= this.scroller.clientWidth + 1) return false;
      const dx = x - drag.x, dy = y - drag.y;
      if (!drag.axis) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 6) return false;
        if (Math.abs(dx) > Math.abs(dy) * 1.1) drag.axis = 'horizontal';
        else if (Math.abs(dy) > Math.abs(dx) * 1.1) drag.axis = 'vertical';
        else return false;
      }
      if (drag.axis !== 'horizontal') return false;
      const elapsed = Math.max(8, time - drag.time);
      drag.velocity = drag.velocity * 0.35 + ((drag.lastX - x) / elapsed) * 0.65;
      drag.lastX = x; drag.time = time;
      this.scroller.scrollLeft = drag.left - dx;
      this.scroller.classList.add('is-dragging');
      this.draggedAt = performance.now();
      this.update();
      return true;
    }
    endDrag() {
      const drag = this.drag; this.drag = null; this.scroller.classList.remove('is-dragging');
      if (!drag || drag.axis !== 'horizontal' || performance.now() - drag.time > 100 || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      let velocity = Math.max(-2, Math.min(2, drag.velocity));
      let previous = performance.now();
      const tick = now => {
        const elapsed = Math.min(32, now - previous); previous = now;
        const before = this.scroller.scrollLeft;
        this.scroller.scrollLeft += velocity * elapsed;
        velocity *= Math.pow(0.9, elapsed / 16.67);
        this.update();
        if (Math.abs(velocity) > 0.02 && Math.abs(this.scroller.scrollLeft - before) > 0.1) this.momentum = requestAnimationFrame(tick);
      };
      if (Math.abs(velocity) > 0.08) this.momentum = requestAnimationFrame(tick);
    }
    step() { return this.querySelector('thead th:nth-child(2)')?.getBoundingClientRect().width || 180; }
    move(direction) {
      cancelAnimationFrame(this.momentum);
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scroller.scrollBy({ left: direction * this.step(), behavior: reduced ? 'instant' : 'smooth' });
    }
    update() {
      const max = Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth), left = Math.max(0, this.scroller.scrollLeft), overflow = max > 1;
      this.querySelector('[data-compare-controls]').hidden = !overflow;
      this.querySelector('[data-compare-prev]').disabled = left <= 1;
      this.querySelector('[data-compare-next]').disabled = left >= max - 1;
      const pages = Math.ceil(max / this.step()) + 1;
      const current = left >= max - 1 ? pages : Math.min(pages, Math.floor((left + 1) / this.step()) + 1);
      this.querySelector('[data-compare-page]').textContent = `${current} / ${pages}`;
      this.scroller.tabIndex = overflow ? 0 : -1;
    }
  });
}
